import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Switch, TextField, Tooltip, Typography } from '@material-ui/core';
import {
  Add,
  DeleteOutline,
  Looks3,
  Looks4,
  Looks5,
  Looks6,
  LooksOne,
  LooksTwo,
  Save,
  Star,
  StarBorder,
} from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';

export interface SavedVolunteerSearch<TFilters> {
  savedVolunteerSearchId: string;
  name: string;
  isShared: boolean;
  isOwner: boolean;
  filters: TFilters;
}

interface SavedSearchesMenuProps<TFilters> {
  activeSearchId: string | null;
  isDirty: boolean;
  getCurrentFilters: () => TFilters;
  onApply: (search: SavedVolunteerSearch<TFilters>) => void;
  onActiveSearchChange: (searchId: string | null) => void;
}

const numberIcons = [LooksOne, LooksTwo, Looks3, Looks4, Looks5, Looks6];

const menuItemStyle: React.CSSProperties = {
  color: '#fff',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 400,
  padding: '4px 8px',
  justifyContent: 'flex-start',
  width: '100%',
};

const rowIconStyle: React.CSSProperties = { color: 'rgba(255, 255, 255, 0.6)' };

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.6)',
  padding: '8px 8px 2px',
};

export function SavedSearchesMenu<TFilters>(props: SavedSearchesMenuProps<TFilters>) {
  const { activeSearchId, isDirty, getCurrentFilters, onApply, onActiveSearchChange } = props;
  const apiUrl = useApiUrl();
  const campfireFetch = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSearches, setSavedSearches] = useState<SavedVolunteerSearch<TFilters>[]>([]);

  const activeSearch = savedSearches.find((s) => s.savedVolunteerSearchId === activeSearchId) ?? null;
  const mySearches = savedSearches.filter((s) => s.isOwner);
  const sharedSearches = savedSearches.filter((s) => !s.isOwner);

  function loadSavedSearches() {
    campfireFetch
      .run({ url: `${apiUrl}/vm/management/saved-searches/list`, method: 'post', data: {} })
      .then((response) => {
        if (response.ok && response.data?.data?.savedSearches) {
          const searches: SavedVolunteerSearch<TFilters>[] = response.data.data.savedSearches;
          setSavedSearches(searches);
        }
      });
  }

  useEffect(() => {
    loadSavedSearches();
  }, []);

  function saveSearch(savedVolunteerSearchId: string | null, searchName: string, shared: boolean) {
    setSaving(true);
    return campfireFetch
      .run({
        url: `${apiUrl}/vm/management/saved-searches/save`,
        method: 'post',
        data: {
          savedVolunteerSearchId,
          name: searchName,
          isShared: shared,
          filters: getCurrentFilters(),
        },
      })
      .then((response) => {
        setSaving(false);
        if (!response.ok) {
          setSnackbar({ open: true, message: 'Unable to save search', variant: 'error' });
          return false;
        }
        onActiveSearchChange(response.data?.data?.savedVolunteerSearchId ?? null);
        setSnackbar({ open: true, message: 'Search saved', variant: 'success' });
        loadSavedSearches();
        return true;
      })
      .catch(() => {
        setSaving(false);
        setSnackbar({ open: true, message: 'Unable to save search', variant: 'error' });
        return false;
      });
  }

  function removeSearch(search: SavedVolunteerSearch<TFilters>) {
    campfireFetch
      .run({
        url: `${apiUrl}/vm/management/saved-searches/remove`,
        method: 'post',
        data: { savedVolunteerSearchId: search.savedVolunteerSearchId },
      })
      .then((response) => {
        if (!response.ok) {
          setSnackbar({ open: true, message: 'Unable to delete search', variant: 'error' });
          return;
        }
        if (search.savedVolunteerSearchId === activeSearchId) {
          onActiveSearchChange(null);
        }
        loadSavedSearches();
      });
  }

  function openSaveDialog() {
    setName('');
    setIsShared(false);
    setOpen(false);
    setSaveDialogOpen(true);
  }

  function handleSaveDialogSubmit() {
    if (!name.trim() || saving) return;
    saveSearch(null, name.trim(), isShared).then((saved) => {
      if (saved) setSaveDialogOpen(false);
    });
  }

  function renderSearchRow(search: SavedVolunteerSearch<TFilters>, index: number) {
    const isActive = search.savedVolunteerSearchId === activeSearchId;
    const NumberIcon = numberIcons[index];
    return (
      <Box key={search.savedVolunteerSearchId} display='flex' alignItems='center' width='100%'>
        <Button
          onClick={() => {
            onApply(search);
            setOpen(false);
          }}
          startIcon={NumberIcon ? <NumberIcon style={{ fontSize: '16px' }} /> : <Box width='16px' height='16px' />}
          style={{ ...menuItemStyle, fontWeight: isActive ? 600 : 400, flex: 1 }}
        >
          {search.name}
        </Button>
        {search.isOwner && isActive && isDirty && (
          <Tooltip title='Update with current filters'>
            <span>
              <IconButton
                size='small'
                disabled={saving}
                onClick={() => {
                  saveSearch(search.savedVolunteerSearchId, search.name, search.isShared);
                  setOpen(false);
                }}
                style={rowIconStyle}
              >
                <Save style={{ fontSize: '16px' }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {search.isOwner && (
          <Tooltip title='Delete'>
            <IconButton size='small' onClick={() => removeSearch(search)} style={rowIconStyle}>
              <DeleteOutline style={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <>
      <TooltipMenu
        open={open}
        onClose={() => setOpen(false)}
        title={
          <Box
            display='flex'
            flexDirection='column'
            style={{ padding: '5px 10px', minWidth: '220px', maxWidth: '320px' }}
          >
            <Button onClick={openSaveDialog} startIcon={<Add style={{ fontSize: '16px' }} />} style={menuItemStyle}>
              Save search
            </Button>
            {mySearches.length > 0 && <Typography style={sectionLabelStyle}>My searches</Typography>}
            {mySearches.map(renderSearchRow)}
            {sharedSearches.length > 0 && <Typography style={sectionLabelStyle}>Shared</Typography>}
            {sharedSearches.map((search, i) => renderSearchRow(search, mySearches.length + i))}
          </Box>
        }
      >
        <Tooltip title={activeSearch ? `${activeSearch.name}${isDirty ? ' (modified)' : ''}` : 'Saved searches'}>
          <Button
            onClick={() => setOpen(true)}
            aria-label='Saved searches'
            style={{
              backgroundColor: 'transparent',
              color: '#646464',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: 0,
              width: '32px',
              height: '32px',
              minWidth: '32px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {activeSearch && !isDirty ? (
              <Star style={{ fontSize: '16px' }} />
            ) : (
              <StarBorder style={{ fontSize: '16px' }} />
            )}
          </Button>
        </Tooltip>
      </TooltipMenu>
      <FullscreenDialog
        open={saveDialogOpen}
        close={() => setSaveDialogOpen(false)}
        title='Save search'
        maxWidth='xs'
        fullWidth
        dialogActions={() => (
          <>
            <TabletButton onClick={() => setSaveDialogOpen(false)} disabled={saving}>
              Cancel
            </TabletButton>
            <TabletButton
              variant='contained'
              color='primary'
              disabled={!name.trim() || saving}
              onClick={handleSaveDialogSubmit}
            >
              Save
            </TabletButton>
          </>
        )}
      >
        <Box mt='20px'>
          <TextField
            autoFocus
            fullWidth
            variant='outlined'
            label='Search name'
            value={name}
            inputProps={{ maxLength: 25 }}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveDialogSubmit();
            }}
          />
        </Box>
        <Box display='flex' alignItems='flex-start' mt='18px' mb='8px'>
          <Switch
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            inputProps={{ 'aria-label': 'Share with team' }}
            color='primary'
          />
          <Box pt='6px'>
            <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#000000b0' }}>Share with team</Typography>
            <Typography style={{ fontSize: '12px', color: '#808080' }}>
              Other managers can apply this search, but only you can update or delete it.
            </Typography>
          </Box>
        </Box>
      </FullscreenDialog>
    </>
  );
}
